import { zodResolver } from '@hookform/resolvers/zod';
import { AppError } from '@repo/error';
import { errorMessage, t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Input } from '@repo/ui/input';
import { PasswordInput } from '@repo/ui/password-input';
import { CheckCircle2Icon, XCircleIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { EMAIL_VERIFICATION_STATE } from '@/lib/constants';
import { authClient, useOptionalSession } from '@/providers/session';
import { type TActivateAccountSchema, ZActivateAccountSchema } from '@/types/auth';

export const ActivatePage = () => {
  useLocale();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activationState, setActivationState] = useState<
    keyof typeof EMAIL_VERIFICATION_STATE | null
  >(null);
  const { refreshSession } = useOptionalSession();
  const token = searchParams.get('token') ?? '';

  const form = useForm<TActivateAccountSchema>({
    defaultValues: {
      token,
      email: '',
      password: '',
      confirmPassword: '',
    },
    mode: 'onBlur',
    resolver: zodResolver(ZActivateAccountSchema, { error: validationError }),
  });

  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = async (data: TActivateAccountSchema) => {
    try {
      const result = await authClient.emailPassword.activate({
        token: data.token,
        email: data.email,
        password: data.password,
      });

      const state = result.state as keyof typeof EMAIL_VERIFICATION_STATE;
      setActivationState(state);

      if (state === EMAIL_VERIFICATION_STATE.VERIFIED) {
        toast.success(t('auth.accountActivated'), {
          get description() {
            return t('auth.yourAccountHasBeenActivatedRedirecting');
          },
        });
        await refreshSession();
        setTimeout(() => void navigate('/o'), 1500);
      }
    } catch (err) {
      const error = AppError.parseError(err);
      toast.error(t('auth.activationFailed'), {
        description: errorMessage(AppError.parse(error)),
      });
    }
  };

  if (activationState === EMAIL_VERIFICATION_STATE.EXPIRED) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md space-y-4 text-center">
          <div className="flex justify-center">
            <XCircleIcon className="h-12 w-12 text-destructive" />
          </div>
          <h1 className="font-bold text-2xl">{t('auth.invitationExpired')}</h1>
          <p className="text-muted-foreground">
            {t('auth.yourInvitationLinkHasExpiredPleaseContactYourOrganization')}{' '}
          </p>
          <Button asChild variant="outline">
            <Link to="/auth/signin">{t('auth.goToSignIn')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (activationState === EMAIL_VERIFICATION_STATE.NOT_FOUND) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md space-y-4 text-center">
          <div className="flex justify-center">
            <XCircleIcon className="h-12 w-12 text-destructive" />
          </div>
          <h1 className="font-bold text-2xl">{t('auth.invalidInvitation')}</h1>
          <p className="text-muted-foreground">
            {t('auth.thisInvitationLinkIsInvalidOrHasAlreadyBeen')}{' '}
          </p>
          <Button asChild variant="outline">
            <Link to="/auth/signin">{t('auth.goToSignIn')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (activationState === EMAIL_VERIFICATION_STATE.VERIFIED) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md space-y-4 text-center">
          <div className="flex justify-center">
            <CheckCircle2Icon className="h-12 w-12 text-green-500" />
          </div>
          <h1 className="font-bold text-2xl">{t('auth.accountActivated2')}</h1>
          <p className="text-muted-foreground">
            {t('auth.yourAccountHasBeenSuccessfullyActivatedYouWillBe')}{' '}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="font-bold text-2xl tracking-tight">{t('auth.activateYourAccount')}</h1>
          <p className="text-muted-foreground text-sm">
            {t('auth.enterYourEmailAndSetAPasswordToActivate')}{' '}
          </p>
        </div>

        <Form {...form}>
          <form className="flex w-full flex-col gap-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <input type="hidden" {...form.register('token')} />

            <fieldset className="flex flex-col gap-y-4" disabled={isSubmitting}>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.emailAddress')}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="email"
                        placeholder="you@example.com"
                        type="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.password')}</FormLabel>
                    <FormControl>
                      <PasswordInput autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.confirmPassword')}</FormLabel>
                    <FormControl>
                      <PasswordInput autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>

            <Button className="mt-2 w-full" loading={isSubmitting} size="lg" type="submit">
              {t('auth.activateAccount')}{' '}
            </Button>
          </form>
        </Form>

        <p className="text-center text-muted-foreground text-sm">
          {t('auth.alreadyHaveAnAccount')}{' '}
          <Link className="text-primary duration-200 hover:opacity-70" to="/auth/signin">
            {t('auth.signIn2')}{' '}
          </Link>
        </p>
      </div>
    </div>
  );
};
